/* ────────────────────────────────────────────────────────────────
   Rainger Payload Calculator — Logic
   Sortable comparison table, compare mode with side-by-side view,
   GVM upgrade simulator. Shareable URL hash.
   Vehicle + gear DATA lives in assets/payload-vehicles.js
   (window.RS_PAYLOAD_DATA) and must load before this file.
   Loaded via templates/page.payload-calculator.liquid.
   ──────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Guard: only run if calculator markup is present on this page
  if (!document.querySelector('.rs-payload-page')) return;

  // ───────────────────────────────────────────────────────────────────────
  // VEHICLE + GEAR DATA — lives in assets/payload-vehicles.js
  // (Rainger Payload Database v6), loaded before this file and exposed on
  // window.RS_PAYLOAD_DATA. Edit that file for data changes, this one for
  // logic.
  // ───────────────────────────────────────────────────────────────────────
  if (!window.RS_PAYLOAD_DATA) {
    console.error("Rainger payload calculator: payload-vehicles.js data not loaded");
    return;
  }
  const {VEHICLES, VEHICLE_FLAGS, GVM_TOPS, BUILDS, ACCESSORIES, findGvmKit} = window.RS_PAYLOAD_DATA;
  // Rows with null kerb/GVM are announced-but-unweighted (prelaunch): they
  // appear in the comparison table with em-dash cells but cannot be selected
  // in the build calculator.
  const CALC_VEHICLES = VEHICLES.filter((v) => v.kerb != null && v.gvm != null);

  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  let selectedBrand = "Toyota";
  let selectedGen = "Current";
  let selectedVehicle = CALC_VEHICLES.find((v) => v.brand === selectedBrand && v.gen === selectedGen) || CALC_VEHICLES[0];
  let selectedBuild = BUILDS[0];
  let tableSort = {col: "payload", dir: "desc"};
  let tableSearch = "";
  let tableGenFilter = "";
  let cols = {axle: false, engine: false, gcm: false};
  let compareMode = false;
  const selectedIds = new Set();
  let highlightDiff = true;

  // ───────────────────────────────────────────────────────────────────────
  // HELPERS
  // ───────────────────────────────────────────────────────────────────────
  const $ = (s) => document.querySelector(s);
  // Null-guard: a null/unpublished figure renders as an em dash, never "0kg"
  // (a zero here reads as a real weight and produces false Over-GVM verdicts).
  const kg = (v) => (v == null || isNaN(Number(v))) ? "—" : `${Math.round(Number(v))}kg`;

  // SVG sort arrows (consistent across platforms, replaces ▲▼↕ unicode)
  const SORT_ARROW_NONE = '<svg class="rs-sort-arrow" width="10" height="14" viewBox="0 0 10 14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 0L1 4h8L5 0zM5 14l4-4H1l4 4z"/></svg>';
  const SORT_ARROW_ASC = '<svg class="rs-sort-arrow" width="10" height="14" viewBox="0 0 10 14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 1l4 6H1l4-6z"/></svg>';
  const SORT_ARROW_DESC = '<svg class="rs-sort-arrow" width="10" height="14" viewBox="0 0 10 14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 13l4-6H1l4 6z"/></svg>';
  // Picker helpers draw from CALC_VEHICLES so prelaunch (null-weight) rows
  // never become selectable; the comparison table still uses full VEHICLES.
  const uniqueBrands = () => [...new Set(CALC_VEHICLES.map((v) => v.brand))];
  const vehiclesForBrandGen = () => CALC_VEHICLES.filter((v) => v.brand === selectedBrand && v.gen === selectedGen);
  const erasForBrand = (b) => [...new Set(CALC_VEHICLES.filter((v) => v.brand === b).map((v) => v.gen))];
  function vehicleById(id) { return CALC_VEHICLES.find((v) => v.id === id); }

  // ───────────────────────────────────────────────────────────────────────
  // CALCULATOR — VEHICLE PICKER
  // ───────────────────────────────────────────────────────────────────────
  function renderMakeButtons() {
    $("#rsMakeButtons").innerHTML = uniqueBrands().map((b) =>
      `<button type="button" class="rs-picker-button${b === selectedBrand ? " active" : ""}" data-brand="${b}">${b}</button>`
    ).join("");
    $("#rsMakeButtons").querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => {
        selectedBrand = btn.dataset.brand;
        const eras = erasForBrand(selectedBrand);
        if (!eras.includes(selectedGen)) selectedGen = eras[0];
        selectedVehicle = vehiclesForBrandGen()[0];
        renderAllCalc();
      });
    });
  }
  function renderGenButtons() {
    const eras = erasForBrand(selectedBrand);
    $("#rsGenButtons").innerHTML = ["Current","Previous"].map((g) => {
      const avail = eras.includes(g);
      return `<button type="button" class="rs-picker-button${g === selectedGen ? " active" : ""}${avail ? "" : " disabled"}" data-gen="${g}"${avail ? "" : " disabled"}>${g} generation</button>`;
    }).join("");
    $("#rsGenButtons").querySelectorAll("button:not(:disabled)").forEach((btn) => {
      btn.addEventListener("click", () => {
        selectedGen = btn.dataset.gen;
        selectedVehicle = vehiclesForBrandGen()[0];
        renderAllCalc();
      });
    });
  }
  function renderVariantCards() {
    const variants = vehiclesForBrandGen();
    $("#rsVariantCards").innerHTML = variants.map((v, i) => {
      const active = selectedVehicle && selectedVehicle.id === v.id ? " active" : "";
      return `<button type="button" class="rs-variant-card${active}" data-i="${i}"><strong>${v.model} — ${v.variant}</strong><span>${v.plat} · ${v.my} · payload ${kg(v.payload)} · tow ${kg(v.tow)}</span></button>`;
    }).join("");
    $("#rsVariantCards").querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => {
        selectedVehicle = vehiclesForBrandGen()[Number(btn.dataset.i)];
        renderVariantCards();
        renderMiniInfo();
        calculate();
      });
    });
  }
  function renderMiniInfo() {
    const v = selectedVehicle;
    $("#rsMiniInfo").innerHTML = `
      <span>Kerb: <strong>${kg(v.kerb)}</strong></span>
      <span>GVM: <strong>${kg(v.gvm)}</strong></span>
      <span>GCM: <strong>${kg(v.gcm)}</strong></span>
      <span>Payload: <strong>${kg(v.payload)}</strong></span>
      <span>FAM: <strong>${kg(v.fam)}</strong></span>
      <span>RAM: <strong>${kg(v.ram)}</strong></span>
      <span>Tow: <strong>${kg(v.tow)}</strong></span>
      <span>Tub: <strong>${kg(v.tub)}</strong></span>
    `;
  }
  function renderBuildCards() {
    const v = selectedVehicle;
    $("#rsBuildCards").innerHTML = BUILDS.map((b) => {
      const active = b.id === selectedBuild.id ? " active" : "";
      let wt = b.weight;
      if (b.tubRemoved) wt -= (v.tub || 0);
      const lbl = wt === 0 ? "Baseline (no added weight)" : (wt > 0 ? `+${kg(wt)} fitted weight` : `${kg(wt)} (tub gone)`);
      return `<button type="button" class="rs-build-card${active}" data-id="${b.id}"><span class="rs-card-kicker">${b.kicker}</span><strong>${b.label}</strong><span>${b.desc}</span><span class="rs-build-weight">${lbl}</span></button>`;
    }).join("");
    $("#rsBuildCards").querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => {
        selectedBuild = BUILDS.find((b) => b.id === btn.dataset.id);
        $("#rsTubRemoved").checked = selectedBuild.tubRemoved;
        renderBuildCards();
        calculate();
      });
    });
  }
  function renderAccessoryList() {
    $("#rsAccessoryList").innerHTML = ACCESSORIES.map((a) => `
      <div class="rs-accessory">
        <input type="checkbox" id="rsacc-${a.id}" data-id="${a.id}"${a.default ? " checked" : ""}>
        <label for="rsacc-${a.id}">${a.label}<span>${a.hint}</span></label>
        <input type="number" min="0" value="${a.defaultKg}" data-weight="${a.id}">
      </div>`).join("");
    $("#rsAccessoryList").querySelectorAll("input").forEach((inp) => {
      inp.addEventListener("input", calculate);
      inp.addEventListener("change", calculate);
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // CORE CALCULATION
  // ───────────────────────────────────────────────────────────────────────
  function getAccessoryTotal() {
    let t = 0;
    document.querySelectorAll(".rs-accessory").forEach((r) => {
      const chk = r.querySelector('input[type="checkbox"]');
      const wgt = r.querySelector('input[type="number"]');
      if (chk.checked) t += Number(wgt.value) || 0;
    });
    return t;
  }
  function getLoadTotal() {
    const pax = Number($("#rsPassengers").value) || 0;
    const water = Number($("#rsWater").value) || 0;
    const fuel = (Number($("#rsExtraFuel").value) || 0) * 0.85;
    const loose = Number($("#rsLoose").value) || 0;
    return pax + water + fuel + loose;
  }
  function getTowing() {
    const towing = $("#rsTowing").checked;
    if (!towing) return {towing:false,atm:0,towball:0};
    const atm = Number($("#rsAtm").value) || 0;
    const pct = Number($("#rsTowballPct").value) || 10;
    const towball = Math.round(atm * pct / 100);
    $("#rsTowballKg").value = towball;
    return {towing,atm,towball};
  }
  function buildWeightFor(build, vehicle, tubRemovedUserOverride) {
    const tubWt = vehicle.tub || 0; // null tub weight (unpublished) = no credit
    let w = build.weight;
    if (build.tubRemoved) w -= tubWt;
    if (tubRemovedUserOverride !== undefined && tubRemovedUserOverride !== build.tubRemoved) {
      if (tubRemovedUserOverride && !build.tubRemoved) w -= tubWt;
      else if (!tubRemovedUserOverride && build.tubRemoved) w += tubWt;
    }
    return w;
  }
  // Animated count-up for the headline payload number (respects reduced motion)
  function countUp(el, to) {
    if (!el) return;
    to = Math.round(to);
    const from = parseInt(el.dataset.v || "0", 10) || 0;
    el.dataset.v = to;
    el.textContent = kg(to); // synchronous guarantee — rAF is throttled in webviews/background tabs
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t0 = performance.now(), dur = 280;
    function step(t) {
      const k = Math.min(1, (t - t0) / dur);
      const val = Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3)));
      el.textContent = kg(val);
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  // Live payload gauge — fills + colour-shifts as load is added
  function updateGauge(used, max, rem) {
    const fill = $("#rsGaugeFill"); if (!fill) return;
    const pct = max > 0 ? Math.min(100, Math.max(0, (used / max) * 100)) : 0;
    fill.style.width = pct + "%";
    fill.classList.remove("amber", "red");
    if (rem < 80) fill.classList.add("red");
    else if (rem < 250) fill.classList.add("amber");
    const pctEl = $("#rsGaugePct"); if (pctEl) pctEl.textContent = Math.round(max > 0 ? (used / max) * 100 : 0) + "%";
    const u = $("#rsGaugeUsed"); if (u) u.textContent = kg(used) + " of build + gear";
    const m = $("#rsGaugeMax"); if (m) m.textContent = rem < 0 ? kg(Math.abs(rem)) + " OVER" : kg(rem) + " left";
  }
  function calculate() {
    const v = selectedVehicle;
    const tubOverride = $("#rsTubRemoved").checked;
    const buildWt = buildWeightFor(selectedBuild, v, tubOverride);
    const accWt = getAccessoryTotal();
    const loadWt = getLoadTotal();
    const tow = getTowing();

    const payloadUsed = buildWt + accWt + loadWt + tow.towball;
    const maxPayload = v.gvm - v.kerb;
    const payRem = maxPayload - payloadUsed;
    const vehicleOnRoad = v.kerb + payloadUsed;
    const gcmUsed = v.kerb + buildWt + accWt + loadWt + tow.atm;
    const gcmHead = v.gcm - gcmUsed;

    countUp($("#rsPayloadRemaining"), payRem);
    $("#rsPayloadOf").textContent = `of ${kg(maxPayload)} factory payload`;
    if (tow.towing) {
      $("#rsGcmHeadroom").textContent = kg(gcmHead);
      $("#rsGcmOf").textContent = `of ${kg(v.gcm)} GCM (with ${kg(tow.atm)} trailer)`;
    } else {
      $("#rsGcmHeadroom").textContent = "—";
      $("#rsGcmOf").textContent = `Not towing · GCM ${kg(v.gcm)}`;
    }
    $("#rsVehicleWeight").textContent = kg(vehicleOnRoad);
    $("#rsBuildLabel").textContent = selectedBuild.label;
    $("#rsBuildSub").textContent = `+${kg(buildWt)} fitted${tubOverride ? " · tub removed" : ""}`;
    updateGauge(payloadUsed, maxPayload, payRem);

    renderStatus(payRem, gcmHead, tow.towing);
    renderCompareInline(v, accWt, loadWt, tow.towball, tubOverride);
    renderGvmSim(v, payRem);
  }
  function renderStatus(payRem, gcmRem, towing) {
    const s = $("#rsStatus");
    s.classList.remove("green","amber","red");
    if (payRem < 0) {
      s.classList.add("red");
      s.innerHTML = `<strong>Over GVM by ${kg(Math.abs(payRem))}.</strong> Remove gear, fit a GVM upgrade, or reduce load. This rig is not legal on AU roads.`;
      return;
    }
    if (towing && gcmRem < 0) {
      s.classList.add("red");
      s.innerHTML = `<strong>Over GCM by ${kg(Math.abs(gcmRem))}.</strong> Combined vehicle + trailer exceeds GCM. Reduce trailer load, vehicle load, or upgrade GCM.`;
      return;
    }
    if (towing && gcmRem < 200) {
      s.classList.add("amber");
      s.innerHTML = `<strong>${kg(gcmRem)} GCM headroom — tight.</strong> Payload looks fine (${kg(payRem)} remaining) but combined weight is close to GCM.`;
      return;
    }
    if (payRem < 80) {
      s.classList.add("red");
      s.innerHTML = `<strong>${kg(payRem)} remaining — too tight.</strong> Bags, dog, or full water tank will push you over. Drop weight or fit a GVM upgrade.`;
      return;
    }
    if (payRem < 250) {
      s.classList.add("amber");
      s.innerHTML = `<strong>${kg(payRem)} remaining — watch it.</strong> Limited headroom. A heavier canopy, extra water, or another passenger will eat this fast.`;
      return;
    }
    s.classList.add("green");
    s.innerHTML = `<strong>${kg(payRem)} remaining — comfortable.</strong> Useful headroom for fuel, water, or a heavier canopy. Verify at a weighbridge before any major trip.`;
  }
  function renderCompareInline(v, accWt, loadWt, towball, tubOverride) {
    $("#rsComparePanel").style.display = "block";
    const maxP = v.gvm - v.kerb;
    const currentRem = maxP - (buildWeightFor(selectedBuild, v, tubOverride) + accWt + loadWt + towball);
    const html = BUILDS.map((b) => {
      const bw = buildWeightFor(b, v);
      const rem = maxP - (bw + accWt + loadWt + towball);
      const isCur = b.id === selectedBuild.id;
      let dl, dc, sub;
      if (isCur) { dl = kg(rem); dc = "current-tag"; sub = "Your current build · remaining payload"; }
      else {
        const d = rem - currentRem;
        dl = d >= 0 ? `+${kg(d)}` : `-${kg(Math.abs(d))}`;
        dc = d >= 0 ? "gain" : "loss";
        sub = `${kg(Math.abs(d))} ${d >= 0 ? "more" : "less"} payload than your current build · ${kg(rem)} would remain`;
      }
      return `<div class="rs-compare-col${isCur ? " current" : ""}"><h4>${b.label}${isCur ? " (current)" : ""}</h4><div class="rs-compare-delta ${dc}">${dl}</div><p>${sub}</p></div>`;
    }).join("");
    $("#rsCompareGrid").innerHTML = html;
  }
  function renderGvmSim(v, payRem) {
    const sim = $("#rsGvmSim");
    const kit = findGvmKit(v);
    if (!kit) { sim.style.display = "none"; return; }
    if (kit.superdutyCapped) {
      sim.style.display = "block";
      $("#rsGvmResult").innerHTML = `
        <strong>No aftermarket GVM upgrade pathway.</strong> The Super Duty is precisely engineered at <strong>4500 kg GVM</strong> — exactly the HVNL light-vehicle threshold. Any aftermarket uplift pushes the vehicle over 4.5 t, which triggers an LR (Light Rigid) truck licence requirement in every Australian state and changes the registration class. Practically: there's no consumer-grade GVM kit for this platform. If you need more capacity, the next step is a light truck, not an upgraded ute.
      `;
      return;
    }
    if (kit.gladiatorThin) {
      sim.style.display = "block";
      $("#rsGvmResult").innerHTML = `
        <strong>Two federally-approved GVM upgrades exist</strong> (Commonwealth ROVER, both In Force as at Aug 2026): DMW Manufacturing (VTA-063089, marketed 3950 kg) and Superior Engineering (VTA-064233, marketed 3.5 t with 4-inch lift). <strong>Both lift GVM above 3500 kg, moving the vehicle into the NB1 category</strong> — registration class and tolling treatment can change (a standard car licence still covers it up to 4500 kg GVM). Confirm the current approval and the registration consequences with the installer before purchase.
      `;
      return;
    }
    if (!kit.new_gvm) { sim.style.display = "none"; return; }
    sim.style.display = "block";
    const uplift = kit.new_gvm - v.gvm;
    const newPay = payRem + uplift;
    // A GVM upgrade without a matching GCM lift quietly reduces towing:
    // trailer available at full GVM = GCM − GVM, and raising GVM alone
    // shrinks it. Show the trade-off instead of "towing unaffected".
    let gcmNote;
    if (kit.new_gcm && kit.new_gcm > v.gcm) {
      gcmNote = ` GCM also lifts to ${kg(kit.new_gcm)} (+${kg(kit.new_gcm - v.gcm)} headroom when towing).`;
    } else {
      const trailerBefore = v.gcm - v.gvm;
      const trailerAfter = v.gcm - kit.new_gvm;
      gcmNote = trailerAfter < trailerBefore
        ? ` GCM stays at ${kg(v.gcm)} — so loaded to the upgraded GVM, trailer capacity within GCM drops to ${kg(Math.max(0, trailerAfter))} (from ${kg(trailerBefore)} at factory GVM). More payload, less towing at full load.`
        : " GCM unchanged.";
    }
    const costNote = kit.cost ? ` Typical fitted cost: ~$${kit.cost.toLocaleString()}.` : "";
    $("#rsGvmResult").innerHTML = `Top kit for your platform: <strong>${kit.brand} ${kit.tier}</strong> — lifts GVM to ${kg(kit.new_gvm)} (+${kg(uplift)} payload headroom). Your remaining payload would become <strong>${kg(newPay)}</strong> instead of ${kg(payRem)}.${gcmNote}${costNote}`;
  }
  function renderAllCalc() {
    renderMakeButtons();
    renderGenButtons();
    renderVariantCards();
    renderMiniInfo();
    renderBuildCards();
    calculate();
  }

  // ───────────────────────────────────────────────────────────────────────
  // COMPARISON TABLE
  // ───────────────────────────────────────────────────────────────────────
  function colDefs() {
    const base = [
      {key:"brand",label:"Brand",sortable:true,visible:tableSort.col !== "brand"},
      {key:"model",label:"Model",sortable:true,visible:true},
      {key:"gen",label:"Gen",sortable:true,visible:true},
      {key:"variant",label:"Variant",sortable:true,visible:true,trunc:true},
      {key:"my",label:"MY",sortable:true,visible:true},
      {key:"kerb",label:"Kerb",sortable:true,visible:true,unit:"kg"},
      {key:"gvm",label:"GVM",sortable:true,visible:true,unit:"kg"},
      {key:"payload",label:"Payload",sortable:true,visible:true,unit:"kg",bold:true},
      {key:"tow",label:"Tow",sortable:true,visible:true,unit:"kg"},
    ];
    if (cols.axle) {
      base.push({key:"fam",label:"FAM",sortable:true,visible:true,unit:"kg"});
      base.push({key:"ram",label:"RAM",sortable:true,visible:true,unit:"kg"});
    }
    if (cols.engine) {
      base.push({key:"kw",label:"kW",sortable:true,visible:true});
      base.push({key:"nm",label:"Nm",sortable:true,visible:true});
    }
    if (cols.gcm) {
      base.push({key:"gcm",label:"GCM",sortable:true,visible:true,unit:"kg"});
      base.push({key:"tball",label:"Tow ball",sortable:true,visible:true,unit:"kg"});
    }
    return base;
  }

  function filteredVehicles() {
    // Forgiving search: trim, brand abbreviations/misspellings, and
    // punctuation/space-insensitive matching (so "dmax"/"d max" find "D-Max",
    // "vw" finds Volkswagen, "izuzu" finds Isuzu).
    const ALIASES = { vw: "volkswagen", izuzu: "isuzu", isusu: "isuzu", chevy: "chevrolet", mitsi: "mitsubishi" };
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const qn = norm(tableSearch.trim().split(/\s+/).map((t) => ALIASES[t.toLowerCase()] || t).join(" "));
    let rows = VEHICLES.filter((v) => {
      const hayn = norm(v.brand + " " + v.model + " " + v.gen + " " + v.plat + " " + v.variant + " " + v.my);
      return (!qn || hayn.includes(qn)) && (!tableGenFilter || v.gen === tableGenFilter);
    });
    rows.sort((a,b) => {
      const k = tableSort.col;
      let av = a[k], bv = b[k];
      if (typeof av === "string" || typeof bv === "string") { av = String(av ?? "").toLowerCase(); bv = String(bv ?? "").toLowerCase(); return tableSort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av); }
      // Unpublished (null) figures always sort to the bottom
      if (av == null) av = tableSort.dir === "asc" ? Infinity : -Infinity;
      if (bv == null) bv = tableSort.dir === "asc" ? Infinity : -Infinity;
      return tableSort.dir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }

  function renderTable() {
    const defs = colDefs();
    const totalCols = defs.filter((d) => d.visible).length + (compareMode ? 1 : 0);

    const headHtml = `<tr>${compareMode ? '<th class="rs-checkbox-cell" scope="col"><span class="rs-visually-hidden" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);">Select</span></th>' : ""}${defs.filter((d) => d.visible).map((d) => {
      const sorted = d.sortable && tableSort.col === d.key;
      const ariaSort = sorted ? (tableSort.dir === "asc" ? "ascending" : "descending") : (d.sortable ? "none" : null);
      const arrow = sorted ? (tableSort.dir === "asc" ? SORT_ARROW_ASC : SORT_ARROW_DESC) : SORT_ARROW_NONE;
      const isPayloadHeader = d.key === "payload";
      const classes = [
        sorted ? "rs-sorted" : "",
        isPayloadHeader ? "payload-header" : "",
      ].filter(Boolean).join(" ");
      const attrs = [
        `data-col="${d.key}"`,
        classes ? `class="${classes}"` : "",
        d.sortable ? `role="columnheader" tabindex="0"` : 'style="cursor:default;"',
        ariaSort ? `aria-sort="${ariaSort}"` : "",
        'scope="col"',
      ].filter(Boolean).join(" ");
      return `<th ${attrs}>${d.label}${d.sortable ? arrow : ""}</th>`;
    }).join("")}</tr>`;
    $("#rsTableHead").innerHTML = headHtml;

    const rows = filteredVehicles();
    const sortedByBrand = tableSort.col === "brand";
    let html = "";
    let lastBrand = null;

    rows.forEach((v) => {
      if (sortedByBrand && v.brand !== lastBrand) {
        html += `<tr class="rs-brand-header"><td colspan="${totalCols}">${v.brand}</td></tr>`;
        lastBrand = v.brand;
      }
      const isSel = selectedIds.has(v.id);
      const isPrelaunch = v.prelaunch === true;
      const classes = [isSel ? "rs-row-selected" : "", isPrelaunch ? "rs-row-prelaunch" : ""].filter(Boolean).join(" ");

      let cells = compareMode ? (isPrelaunch
        ? '<td class="rs-checkbox-cell"></td>'
        : `<td class="rs-checkbox-cell"><label><input type="checkbox" data-vid="${v.id}" ${isSel ? "checked" : ""} aria-label="Select ${v.brand} ${v.model} ${v.variant} for comparison"></label></td>`) : "";
      defs.filter((d) => d.visible).forEach((d) => {
        let val = v[d.key];
        let cellHtml = "";
        if (d.key === "variant" && d.trunc) {
          const flag = VEHICLE_FLAGS[v.id];
          // Tub width between arches — shown only where the manufacturer
          // publishes a figure (null = not published, line omitted)
          const archLine = v.arch ? `<div class="rs-popover-meta">Tub width between arches: ${v.arch}mm</div>` : "";
          cellHtml = `<button type="button" class="rs-variant-trunc" aria-expanded="false" aria-label="${v.model} ${val} details">${val}<span class="rs-variant-popover" role="tooltip"><strong>${v.model} — ${val}</strong>${v.plat} · ${v.my}<div class="rs-popover-meta">Kerb ${kg(v.kerb)} · GVM ${kg(v.gvm)} · Payload ${kg(v.payload)} · Tow ${kg(v.tow)}</div>${archLine}${flag ? `<span class="rs-popover-flag">${flag}</span>` : ""}</span></button>`;
        } else if (d.key === "gen") {
          const ec = val === "Current" ? "rs-era-current" : "rs-era-previous";
          cellHtml = `<span class="rs-era-pill ${ec}">${val}</span>`;
        } else if (d.unit === "kg") {
          cellHtml = kg(val);
        } else {
          cellHtml = String(val);
        }
        cells += `<td${d.bold ? ' class="payload"' : ""}>${cellHtml}</td>`;
      });
      html += `<tr class="${classes}" data-vid="${v.id}">${cells}</tr>`;
    });

    if (rows.length === 0) {
      html = `<tr><td colspan="${totalCols}" style="text-align:center;padding:24px;color:var(--rs-muted);">No utes match your filters.</td></tr>`;
    }
    $("#rsTableBody").innerHTML = html;

    // Wire header sort (click + keyboard for a11y)
    $("#rsTableHead").querySelectorAll('th[role="columnheader"]').forEach((th) => {
      const doSort = () => {
        const c = th.dataset.col;
        if (tableSort.col === c) tableSort.dir = tableSort.dir === "asc" ? "desc" : "asc";
        else { tableSort.col = c; tableSort.dir = (c === "payload" || c === "gvm" || c === "tow" || c === "gcm") ? "desc" : "asc"; }
        renderTable();
      };
      th.addEventListener("click", doSort);
      th.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); doSort(); }
      });
    });

    // Wire variant popover toggle (tap-friendly on mobile, hover for desktop)
    $("#rsTableBody").querySelectorAll(".rs-variant-trunc").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = btn.getAttribute("aria-expanded") === "true";
        $("#rsTableBody").querySelectorAll('.rs-variant-trunc[aria-expanded="true"]').forEach((other) => {
          if (other !== btn) other.setAttribute("aria-expanded", "false");
        });
        btn.setAttribute("aria-expanded", isOpen ? "false" : "true");
      });
    });

    if (compareMode) {
      $("#rsTableBody").querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.addEventListener("change", () => {
          const vid = cb.dataset.vid;
          if (cb.checked) {
            if (selectedIds.size >= 4) { cb.checked = false; alert("Max 4 utes can be compared at once. Untick one to add another."); return; }
            selectedIds.add(vid);
          } else selectedIds.delete(vid);
          updateCompareBar();
          updateShareUrl();
          cb.closest("tr").classList.toggle("rs-row-selected", cb.checked);
        });
      });
    }
  }

  function updateCompareBar() {
    const bar = $("#rsCompareBar");
    const n = selectedIds.size;
    if (n === 0 || !compareMode) { bar.classList.add("hidden"); return; }
    bar.classList.remove("hidden");
    $("#rsCompareCount").textContent = `${n} ute${n === 1 ? "" : "s"} selected`;
    $("#rsShowCompare").disabled = n < 2;
    $("#rsShowCompare").style.opacity = n < 2 ? "0.5" : "1";
  }

  function updateShareUrl() {
    const ids = [...selectedIds].join(",");
    const base = window.location.href.split("#")[0];
    const url = ids ? `${base}#compare=${ids}` : base;
    $("#rsShareUrl").value = url;
    if (ids) history.replaceState(null, "", `#compare=${ids}`);
    else if (window.location.hash.startsWith("#compare=")) history.replaceState(null, "", base);
  }

  function showCompareView() {
    if (selectedIds.size < 2) return;
    const view = $("#rsCompareView");
    view.classList.add("active");
    view.scrollIntoView({behavior:"smooth", block:"start"});
    renderCompareTable();
  }

  function renderCompareTable() {
    const ids = [...selectedIds];
    const utes = ids.map(vehicleById).filter(Boolean);
    $("#rsCompareViewTitle").textContent = `Comparing ${utes.length} ute${utes.length === 1 ? "" : "s"}`;

    const specs = [
      {key:"brand",label:"Brand",fmt:(v) => v.brand},
      {key:"model",label:"Model",fmt:(v) => v.model},
      {key:"variant",label:"Variant",fmt:(v) => v.variant},
      {key:"gen",label:"Generation",fmt:(v) => `<span class="rs-era-pill ${v.gen === "Current" ? "rs-era-current" : "rs-era-previous"}">${v.gen}</span> ${v.plat}`},
      {key:"my",label:"Model year",fmt:(v) => v.my},
      {key:"kerb",label:"Kerb weight",fmt:(v) => kg(v.kerb),diff:true},
      {key:"gvm",label:"GVM",fmt:(v) => kg(v.gvm),diff:true},
      {key:"gcm",label:"GCM",fmt:(v) => kg(v.gcm),diff:true},
      {key:"payload",label:"Payload",fmt:(v) => `<strong style="color:var(--rs-rainger);font-size:16px;">${kg(v.payload)}</strong>`,diff:true,nofmt:true,raw:(v) => v.payload},
      {key:"fam",label:"Front axle max",fmt:(v) => kg(v.fam),diff:true},
      {key:"ram",label:"Rear axle max",fmt:(v) => kg(v.ram),diff:true},
      {key:"tow",label:"Braked tow",fmt:(v) => kg(v.tow),diff:true},
      {key:"tball",label:"Max tow ball",fmt:(v) => kg(v.tball),diff:true},
      {key:"wb",label:"Wheelbase",fmt:(v) => v.wb == null ? "—" : `${v.wb} mm`,diff:true},
      {key:"engine",label:"Engine",fmt:(v) => v.kw == null ? "—" : `${v.kw} kW / ${v.nm == null ? "—" : v.nm} Nm`,diff:false},
      {key:"tub",label:"Tub weight est.",fmt:(v) => kg(v.tub),diff:false},
      {key:"arch",label:"Tub width between arches",fmt:(v) => v.arch == null ? "Not published" : `${v.arch} mm`,diff:false},
    ];

    let html = `<thead><tr><th>Spec</th>${utes.map((v) => `<th><div class="rs-cell-stack"><span>${v.brand} ${v.model}</span><small>${v.variant} · ${v.my}</small></div></th>`).join("")}</tr></thead>`;
    html += `<tbody>`;
    specs.forEach((spec) => {
      const values = utes.map((v) => spec.fmt(v));
      let isDiff = false;
      if (highlightDiff && spec.diff) {
        const raw = utes.map((v) => Number(v[spec.key]) || 0);
        const min = Math.min(...raw);
        const max = Math.max(...raw);
        const diff = max - min;
        const pct = min > 0 ? diff / min * 100 : 100;
        if (diff > 50 || pct > 5) isDiff = true;
      }
      html += `<tr class="${isDiff ? "rs-diff" : ""}"><th>${spec.label}</th>${values.map((val) => `<td>${val}</td>`).join("")}</tr>`;
    });
    html += `</tbody>`;
    $("#rsCompareTable").innerHTML = html;
    updateShareUrl();
  }

  function hideCompareView() {
    $("#rsCompareView").classList.remove("active");
  }

  function clearCompareSelection() {
    selectedIds.clear();
    updateCompareBar();
    updateShareUrl();
    hideCompareView();
    renderTable();
  }

  function loadFromHash() {
    const hash = window.location.hash;
    const m = hash.match(/^#compare=([\w\-,]+)$/);
    if (m) {
      const ids = m[1].split(",").filter((id) => vehicleById(id));
      if (ids.length >= 2 && ids.length <= 4) {
        compareMode = true;
        $("#rsToggleCompare").checked = true;
        $("#rsToggleCompareLabel").classList.add("active");
        ids.forEach((id) => selectedIds.add(id));
        renderTable();
        updateCompareBar();
        showCompareView();
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // STANDOUT RANKINGS — highest/lowest payload tables (#best-worst)
  // Rendered from the database so they can never drift from it (A8 item J,
  // 2026-08-30 — the hardcoded versions silently desynced when rows were
  // added). Scope matches the section copy: current generation, 4x4
  // dual-cab pick-ups; Super Duty excluded (its own class, per the page),
  // cab-chassis and 4x2 excluded, announced-only rows excluded.
  // ───────────────────────────────────────────────────────────────────────
  function renderStandouts() {
    const hi = $("#rsRankHigh"), lo = $("#rsRankLow");
    if (!hi || !lo) return;
    const pool = VEHICLES.filter((v) =>
      v.gen === "Current" && v.payload != null && !v.prelaunch &&
      !/chassis/i.test(v.variant) && !/4x2/i.test(v.variant) &&
      !v.id.includes("FORD-RANGER-SD"));
    const row = (v, i, cls) =>
      `<tr><td class="rs-rank-num">${i + 1}</td><td class="rs-rank-variant">${v.brand} ${v.model} <span>${v.variant}</span></td><td class="${cls}">${v.payload} kg</td></tr>`;
    const byPayload = (dir) =>
      [...pool].sort((a, b) => dir * (a.payload - b.payload) || a.brand.localeCompare(b.brand));
    hi.innerHTML = byPayload(-1).slice(0, 5).map((v, i) => row(v, i, "rs-rank-good")).join("");
    lo.innerHTML = byPayload(1).slice(0, 5).map((v, i) => row(v, i, "rs-rank-bad")).join("");
  }

  // ───────────────────────────────────────────────────────────────────────
  // INIT
  // ───────────────────────────────────────────────────────────────────────
  renderAccessoryList();
  renderAllCalc();
  renderTable();
  renderStandouts();

  ["#rsPassengers","#rsWater","#rsExtraFuel","#rsLoose","#rsAtm","#rsTowballPct"].forEach((sel) => {
    const el = $(sel);
    if (!el) return;
    el.addEventListener("input", calculate);
    el.addEventListener("change", calculate);
  });
  $("#rsTubRemoved").addEventListener("change", () => { renderBuildCards(); calculate(); });
  $("#rsTowing").addEventListener("change", () => {
    $("#rsTowingPanel").classList.toggle("active", $("#rsTowing").checked);
    calculate();
  });

  $("#rsSearch").addEventListener("input", (e) => { tableSearch = e.target.value; renderTable(); });
  $("#rsGenFilter").addEventListener("change", (e) => { tableGenFilter = e.target.value; renderTable(); });

  function bindColToggle(inputSel, labelSel, key) {
    const inp = $(inputSel), lab = $(labelSel);
    inp.addEventListener("change", () => {
      cols[key] = inp.checked;
      lab.classList.toggle("active", inp.checked);
      renderTable();
    });
  }
  bindColToggle("#rsToggleAxle","#rsToggleAxleLabel","axle");
  bindColToggle("#rsToggleEngine","#rsToggleEngineLabel","engine");
  bindColToggle("#rsToggleGcm","#rsToggleGcmLabel","gcm");

  $("#rsToggleCompare").addEventListener("change", (e) => {
    compareMode = e.target.checked;
    $("#rsToggleCompareLabel").classList.toggle("active", compareMode);
    if (!compareMode) {
      selectedIds.clear();
      updateShareUrl();
      hideCompareView();
    }
    renderTable();
    updateCompareBar();
  });

  $("#rsClearCompare").addEventListener("click", clearCompareSelection);
  $("#rsShowCompare").addEventListener("click", showCompareView);
  $("#rsCloseCompare").addEventListener("click", hideCompareView);
  $("#rsHighlightDiff").addEventListener("change", (e) => {
    highlightDiff = e.target.checked;
    $("#rsHighlightDiffLabel").classList.toggle("active", highlightDiff);
    renderCompareTable();
  });
  $("#rsHighlightDiffLabel").classList.toggle("active", highlightDiff);

  $("#rsShareUrl").addEventListener("click", function() { this.select(); document.execCommand && document.execCommand("copy"); });

  // Close any open variant popover when clicking outside, or pressing Escape
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".rs-variant-trunc")) {
      document.querySelectorAll('.rs-variant-trunc[aria-expanded="true"]').forEach((b) => b.setAttribute("aria-expanded", "false"));
    }
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      document.querySelectorAll('.rs-variant-trunc[aria-expanded="true"]').forEach((b) => b.setAttribute("aria-expanded", "false"));
    }
  });

  loadFromHash();
})();
